/** Stored on `user_settings.language` and sent to generation workers. */
export const LESSON_LANGUAGE_LABELS = ["English", "Arabic"] as const;

export type LessonLanguage = (typeof LESSON_LANGUAGE_LABELS)[number];

export const DEFAULT_LESSON_LANGUAGE: LessonLanguage = "English";
export const SOURCE_LESSON_LANGUAGE: LessonLanguage = "English";

/** UI select values on settings / course pages. */
export type PreferredLanguageKey = "english" | "arabic";

export const PREFERRED_LANGUAGE_KEY_TO_LABEL: Record<PreferredLanguageKey, LessonLanguage> = {
  english: "English",
  arabic: "Arabic",
};

export function normalizeLessonLanguage(value: string | null | undefined): LessonLanguage {
  const trimmed = value?.trim();
  if (trimmed === "Arabic") return "Arabic";
  return DEFAULT_LESSON_LANGUAGE;
}

export function preferredLanguageKeyFromLabel(
  label: string | null | undefined,
): PreferredLanguageKey {
  return normalizeLessonLanguage(label) === "Arabic" ? "arabic" : "english";
}

/** True only for Arabic stored in DB (English and unknown values are LTR). */
export function isRtlLessonLanguage(language: string | null | undefined): boolean {
  return normalizeLessonLanguage(language) === "Arabic";
}

/** Use on lesson/quiz/remedial content roots: RTL only when language is Arabic. */
export function lessonContentDirection(language: string | null | undefined): "rtl" | "ltr" {
  return isRtlLessonLanguage(language) ? "rtl" : "ltr";
}

export function isValidLessonLanguageInput(
  value: unknown,
): value is LessonLanguage {
  return value === "English" || value === "Arabic";
}

export function normalizeAvailableLanguages(
  values: string[] | null | undefined,
): LessonLanguage[] {
  const known = (values ?? []).filter(isValidLessonLanguageInput);
  const ordered = LESSON_LANGUAGE_LABELS.filter((label) => known.includes(label));
  return ordered.includes(SOURCE_LESSON_LANGUAGE)
    ? ordered
    : [SOURCE_LESSON_LANGUAGE, ...ordered];
}

